// es-AR number formatting for the performance dashboard. Thousands use a dot,
// decimals a comma (259.552.194 · 69,7 %). Money is intentionally NOT here: it
// stays company-aware via useCompanyCurrencyFormatter, so we never hardcode a
// currency/locale for a tenant.

const LOCALE = 'es-AR';

export const fmtNumber = (n: number, decimals = 0): string =>
  new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

// Signed plain number, e.g. "+3" / "−5". Zero renders without a sign.
export const fmtSignedNumber = (n: number, decimals = 0): string =>
  new Intl.NumberFormat(LOCALE, {
    signDisplay: 'exceptZero',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

// Percentage from a 0..1 fraction, e.g. fmtPct(0.697) -> "69,7 %".
export const fmtPct = (n: number, decimals = 1): string =>
  new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

// A share with one decimal that never reads empty: a real but tiny one shows
// "<0,1 %" instead of rounding to "0,0 %".
export const fmtSharePct = (n: number): string =>
  n > 0 && n < 0.0005 ? `<${fmtPct(0.001, 1)}` : fmtPct(n, 1);

// Signed percentage from a 0..1 fraction, e.g. fmtSignedPct(0.123) -> "+12,3 %".
export const fmtSignedPct = (n: number, decimals = 1): string =>
  new Intl.NumberFormat(LOCALE, {
    style: 'percent',
    signDisplay: 'exceptZero',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);

// Month of a UTC date, e.g. "junio" or, with the year, "junio de 2026".
export const fmtMonthName = (date: Date, withYear = false): string =>
  new Intl.DateTimeFormat(LOCALE, {
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(date);

// Local time of day of an instant, e.g. "10:32".
export const fmtTime = (date: Date): string =>
  new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

// Local day and month of an instant, e.g. "25/10".
export const fmtDayMonth = (date: Date): string =>
  new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
  }).format(date);

// Calendar date of an ISO day, e.g. "21/09/2026".
export const fmtCalendarDate = (isoDay: string): string =>
  new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(isoDay));
